import { z } from "zod";
import { ISODateSchema, ULIDSchema } from "./primitives.js";
import { UserIdSchema } from "./user.js";
/** The four taxonomy kinds. The ReferenceData table also holds rows under
 *  setting#, usage#, invite#, inviteIdx#, rate*#, idem# — those are NOT created
 *  by the /taxonomy endpoints and this enum explicitly excludes them. */
export const TaxonomyKindSchema = z.enum(["tag", "category", "cuisine", "diet"]);
// 26-char Crockford32 body — matches ULID_BODY in ulid.ts. Used for SK construction.
const UlidBodySchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, "must be a 26-char Crockford32 ULID body");
const NameSchema = z.string().min(1, "name is required").max(64, "name must be ≤ 64 chars");
// Builder for per-kind row schema. Keeps the four shapes DRY without losing
// per-kind type narrowing via `kind` discriminator.
function makeRowSchema(kind, includeColor) {
    const skSchema = z
        .string()
        .refine((v) => v.startsWith(`${kind}#`), { message: `sk must start with ${kind}#` })
        .refine((v) => UlidBodySchema.safeParse(v.slice(kind.length + 1)).success, { message: "sk body must be a 26-char ULID body" });
    return z
        .object({
        householdId: ULIDSchema("hh"),
        sk: skSchema,
        id: ULIDSchema(kind),
        kind: z.literal(kind),
        schemaVersion: z.literal(1),
        name: NameSchema,
        // `.default(null)` tolerates a legacy tag row written before `color` existed
        // (missing attribute → undefined). Without it, one color-less tag 500s the
        // whole get-taxonomy list. Matches TaxonomyApiItemSchema's color tolerance.
        ...(includeColor ? { color: z.string().max(32).nullable().default(null) } : {}),
        parentId: ULIDSchema(kind).nullable(),
        createdBy: UserIdSchema,
        createdAt: ISODateSchema,
        updatedAt: ISODateSchema,
        deletedAt: ISODateSchema.nullable(),
        // ttl is set only on soft-delete; absent on live rows. Passthrough tolerates either way.
    })
        .passthrough();
}
export const TagRowSchema = makeRowSchema("tag", true);
export const CategoryRowSchema = makeRowSchema("category", false);
export const CuisineRowSchema = makeRowSchema("cuisine", false);
export const DietRowSchema = makeRowSchema("diet", false);
// Discriminated union — narrows on `kind` so callers don't need a type-guard chain.
export const TaxonomyRowSchema = z.discriminatedUnion("kind", [
    TagRowSchema,
    CategoryRowSchema,
    CuisineRowSchema,
    DietRowSchema,
]);
// ----- API request / response shapes -----
const trimmedNonEmpty = z
    .string()
    .min(1, "name is required")
    .max(64, "name must be ≤ 64 chars")
    .refine((s) => s.trim().length > 0, { message: "name must not be whitespace-only" });
// Request body for POST /taxonomy. Color is only valid for kind="tag".
export const CreateTaxonomyRequestSchema = z
    .object({
    kind: TaxonomyKindSchema,
    name: trimmedNonEmpty,
    color: z.string().max(32).optional(),
})
    .strict()
    .refine((v) => !(v.color !== undefined && v.kind !== "tag"), {
    message: "color is only allowed when kind=tag",
    path: ["color"],
});
// Request body for PATCH /taxonomy/{kind}/{id}. At least one field required.
export const PatchTaxonomyRequestSchema = z
    .object({
    name: trimmedNonEmpty.optional(),
    // color is nullable here so the user can explicitly clear it.
    color: z.string().max(32).nullable().optional(),
})
    .strict()
    .refine((v) => v.name !== undefined || v.color !== undefined, {
    message: "at least one of name or color is required",
});
// API response item — derived from the row but without internal keys (householdId, sk).
// Color is always present in the JSON shape (null for non-tag kinds).
export const TaxonomyApiItemSchema = z
    .object({
    id: z.string(),
    kind: TaxonomyKindSchema,
    name: NameSchema,
    color: z.string().max(32).nullable().default(null),
    parentId: z.string().nullable().default(null),
    createdAt: ISODateSchema,
    updatedAt: ISODateSchema,
    deletedAt: ISODateSchema.nullable(),
})
    .passthrough();
export const TaxonomyListResponseSchema = z.object({
    items: z.array(TaxonomyApiItemSchema),
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=taxonomy.js.map